import { createClient } from './ai_client';
import { DEFAULT_MODEL } from './config';
import { functionToolCalls } from './function_tool_calls';
import { hasCompleteArguments } from './has_complete_arguments';
import { RawApiMessage } from './message';
import { ResolvedToolDefinition } from './tool_definition';

export interface CompletionStreamOptions {
  instanceId?: string;
  tools?: ResolvedToolDefinition[];
  model?: string;
  temperature?: number;
  useAnonymously?: boolean;
}

export type CompletionService = (
  messages: RawApiMessage[],
  options: CompletionStreamOptions,
) => AsyncIterable<RawApiMessage>;

let completionService: CompletionService = defaultCompletionService;

// for test purposes
export function setCompletionService(service: CompletionService) {
  completionService = service;
}

export function resetCompletionService() {
  completionService = defaultCompletionService;
}

export function createCompletionStream(
  messages: RawApiMessage[],
  options: CompletionStreamOptions,
): AsyncIterable<RawApiMessage> {
  return completionService(messages, options);
}

async function* defaultCompletionService(
  messages: RawApiMessage[],
  options: CompletionStreamOptions,
): AsyncIterable<RawApiMessage> {
  const { instanceId, tools, model, temperature, useAnonymously } = options;

  const client = createClient({
    instanceId,
    useAnonymously,
    defaultHeaders: { Prefer: 'cache-ttl=300' },
  });

  const stream = client.chat.completions.stream({
    model: model ?? DEFAULT_MODEL,
    messages: filterInvalidToolCalls(messages),
    temperature,
    stream: true,
    stream_options: { include_usage: true },
    tools: toApiTools(tools),
  });

  for await (const _ of stream) {
    const current = stream.currentChatCompletionSnapshot?.choices[0];
    const partialMessage = current
      ? extractPartialMessage(current.message)
      : undefined;

    if (partialMessage) yield partialMessage;
  }

  const completion = await stream.finalChatCompletion();
  const wasTruncated = completion.choices[0]?.finish_reason === 'length';
  const finalMessage = stream.messages[stream.messages.length - 1];
  // the stream produced no assistant message, so there is no answer to yield
  if (finalMessage?.role !== 'assistant') return;

  yield finalMessage;

  if (wasTruncated && !hasCompleteToolCall(finalMessage)) {
    throw new Error(
      'The response was cut off because it reached the maximum length.',
    );
  }
}

function toApiTools(tools: ResolvedToolDefinition[] | undefined) {
  if (!tools?.length) return undefined;

  return tools.map(({ name, description, parameters }) => ({
    type: 'function' as const,
    function: { name, description, parameters },
  }));
}

function hasCompleteToolCall(message: RawApiMessage): boolean {
  return functionToolCalls(message).some(hasCompleteArguments);
}

interface PartialMessage {
  content?: string | null;
  role?: 'system' | 'user' | 'assistant' | 'function' | 'tool' | 'developer';
  tool_calls?: Array<{
    id: string;
    function: { name: string; arguments: string };
    type: 'function';
  }>;
}

function extractPartialMessage(
  message: PartialMessage,
): RawApiMessage | undefined {
  if (message.role !== 'assistant') return;

  return {
    role: 'assistant',
    content: message.content,
    tool_calls: message.tool_calls,
  };
}

function filterInvalidToolCalls(messages: RawApiMessage[]): RawApiMessage[] {
  return messages.map((message) => {
    if (message.role !== 'assistant') return message;

    // truncated tool calls have invalid JSON arguments, don't send them back to the model
    const toolCalls = functionToolCalls(message).filter(hasCompleteArguments);

    // there is some bug in the OpenAI library:
    // it produces tool_calls: null but does not accept that back
    // so we need to filter them out
    return toolCalls.length > 0
      ? {
          role: message.role,
          content: message.content,
          tool_calls: toolCalls,
        }
      : { role: message.role, content: message.content };
  });
}
